import type { FunnelDimensionResult, FunnelResult, FunnelSegment, FunnelStepResult } from '../api/types'
import { formatChange } from '../format'

interface Props {
  funnel: FunnelResult
}

function tone(percent: number): 'up' | 'down' | 'flat' {
  if (percent > 0) return 'up'
  if (percent < 0) return 'down'
  return 'flat'
}

function count(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

function rate(value: number): string {
  return `${value.toFixed(1)}%`
}

/**
 * One ordered funnel: the whole path, then the same path per dimension value.
 *
 * The bar for each step is its share of the *entry* step, not of the step before it,
 * so the visual reads as "what is left" rather than as a series of unrelated ratios —
 * the shape of the funnel is the point of drawing it.
 *
 * Nothing here computes: every rate, drop-off and share arrives from the backend, for
 * the same reason the evidence package does. A number rendered here and a number the
 * agent narrates must be the same number.
 */
export function FunnelReport({ funnel }: Props) {
  if (funnel.status === 'no_data') {
    return (
      <div className="card">
        <h3 className="funnel__title">{funnel.label}</h3>
        {funnel.notes.map((note) => (
          <p className="breakdown__note" key={note}>
            {note}
          </p>
        ))}
      </div>
    )
  }

  const worst = funnel.steps
    .filter((step) => step.position > 0)
    .reduce<FunnelStepResult | null>(
      (leader, step) => (leader && leader.drop_off_percent >= step.drop_off_percent ? leader : step),
      null,
    )

  return (
    <article className="card funnel">
      <div className="funnel__head">
        <h3 className="funnel__title">{funnel.label}</h3>
        <span className="section__note">vs {funnel.baseline_label}</span>
      </div>

      {worst && (
        <p className="funnel__lead">
          Largest break at <strong>{worst.label.toLowerCase()}</strong> — {rate(worst.drop_off_percent)}{' '}
          of the previous step lost ({count(worst.drop_off_sessions)} sessions).
        </p>
      )}

      <Steps steps={funnel.steps} highlight={worst?.event_name} />

      {funnel.breakdowns.map((breakdown) => (
        <Breakdown key={breakdown.dimension} breakdown={breakdown} />
      ))}

      {funnel.notes.map((note) => (
        <p className="breakdown__note" key={note}>
          {note}
        </p>
      ))}
    </article>
  )
}

/** The funnel as bars, each sized against the entry step. */
function Steps({ steps, highlight }: { steps: FunnelStepResult[]; highlight?: string }) {
  return (
    <ol className="funnel__steps">
      {steps.map((step) => (
        <li
          className={`funnel__step${step.event_name === highlight ? ' funnel__step--worst' : ''}`}
          key={step.event_name}
        >
          <div className="funnel__step-head">
            <span className="funnel__step-label">{step.label}</span>
            <span className="funnel__step-value">{count(step.sessions)}</span>
            <span className={`tile__delta tile__delta--${tone(step.sessions_change_percent)}`}>
              {formatChange(step.sessions_change_percent)}
            </span>
          </div>

          <div
            className="funnel__bar"
            role="img"
            aria-label={`${rate(step.overall_rate_percent)} of sessions that entered the funnel`}
          >
            <span className="funnel__bar-fill" style={{ width: `${step.overall_rate_percent}%` }} />
          </div>

          <div className="funnel__step-foot">
            <span>{rate(step.overall_rate_percent)} of entries</span>
            {step.position > 0 && (
              <>
                <span aria-hidden="true">·</span>
                <span>
                  {rate(step.step_rate_percent)} from previous{' '}
                  <span className={`tile__delta--${tone(step.step_rate_change_percent)}`}>
                    {formatChange(step.step_rate_change_percent)}
                  </span>
                </span>
                <span aria-hidden="true">·</span>
                <span className="funnel__drop">−{count(step.drop_off_sessions)} lost</span>
              </>
            )}
          </div>
        </li>
      ))}
    </ol>
  )
}

/**
 * One dimensional split, collapsed by default.
 *
 * The summary carries the segment losing the most, because that is the reason to open
 * it — a payment method failing, an entry point that never converts.
 */
function Breakdown({ breakdown }: { breakdown: FunnelDimensionResult }) {
  const ranked = breakdown.segments.filter((item) => item.biggest_drop_label)
  const worst =
    ranked.length > 1
      ? ranked.reduce((leader, item) =>
          item.biggest_drop_percent > leader.biggest_drop_percent ? item : leader,
        )
      : null

  return (
    <details className="breakdown">
      <summary className="breakdown__summary">
        <span className="breakdown__chevron" aria-hidden="true" />
        <strong className="breakdown__dimension">By {breakdown.label.toLowerCase()}</strong>
        {worst && (
          <span className="breakdown__lead">
            {worst.segment} loses {rate(worst.biggest_drop_percent)} at{' '}
            {worst.biggest_drop_label.toLowerCase()}
          </span>
        )}
      </summary>

      {breakdown.note && <p className="breakdown__note">{breakdown.note}</p>}

      {/* An empty table and a suppressed one look identical but mean opposite things,
          so a split with nothing to show says why instead of rendering blank headers. */}
      {breakdown.segments.length === 0 ? (
        <p className="breakdown__note">
          {breakdown.suppressed_segments > 0
            ? `Nothing shown: every value was below this dimension's reporting floor.`
            : `No values were returned for this dimension on the reporting day.`}
        </p>
      ) : (
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">{breakdown.label}</th>
              <th scope="col">Share</th>
              {(breakdown.segments[0]?.steps ?? []).map((step) => (
                <th scope="col" key={step.event_name}>
                  {step.label}
                </th>
              ))}
              <th scope="col">End to end</th>
            </tr>
          </thead>
          <tbody>
            {breakdown.segments.map((segment) => (
              <SegmentRow key={segment.segment} segment={segment} />
            ))}
          </tbody>
          {/* Measured with no split, so it sits either side of the rows above: below them
              where sessions touched more than one value, above them where values were
              withheld by the floor. Never their sum. */}
          {breakdown.total && (
            <tfoot>
              <SegmentRow segment={breakdown.total} isTotal />
            </tfoot>
          )}
        </table>
      </div>
      )}
    </details>
  )
}

function SegmentRow({ segment, isTotal = false }: { segment: FunnelSegment; isTotal?: boolean }) {
  const last = segment.steps[segment.steps.length - 1]
  return (
    <tr className={isTotal ? 'table__total' : undefined}>
      <th scope="row">{segment.segment}</th>
      <td title={isTotal ? 'A share of the total out of the total is not a reading.' : undefined}>
        {isTotal ? '—' : rate(segment.share_of_entries_percent)}
      </td>
      {segment.steps.map((step) => (
        <td key={step.event_name}>
          {count(step.sessions)}
          {/* The entry step is 100% by definition, so a rate there would be noise. */}
          {step.position > 0 && (
            <span className="funnel__cell-rate"> {rate(step.step_rate_percent)}</span>
          )}
        </td>
      ))}
      <td>{last ? rate(last.overall_rate_percent) : '—'}</td>
    </tr>
  )
}
